import modelBaseV2 from "../model-base-v2.model";

/**
 * Incentive Input Model
 *
 * API wrappers for the FFIS input routes (the 9 OPERATOR routes of api/incentive/upload and
 * /adjustment). Every one is mapped to menu WM021102 "Inputs & Uploads" and returns 403 until
 * that menu is granted to the caller's role, per country, in UserRoleMenuMap.
 *
 * Country and user are NOT sent from here. The backend reads them from the session, and each
 * stored procedure scopes its reads and writes by country.
 *
 * ============================================================================================
 * THE UPLOAD LIFECYCLE - WHY NOTHING TAKES EFFECT ON UPLOAD
 * ============================================================================================
 * An upload STAGES its rows with Active = 0. They do not enter any calculation. Only
 * validateBatch() flips them to Active = 1, and in the same transaction it marks the PREVIOUS
 * batch for that scope SUPERSEDED - so re-uploading a corrected file leaves one live set, not
 * two overlapping ones.
 *
 * That two-step exists so a wrong file can be inspected and discarded before it changes
 * anybody's pay. A UI that uploads and validates on one click throws that away, so the two are
 * separate calls here and must stay separate buttons.
 *
 * @author FFIS
 */
const IncentiveInputModel = {

  /* ---- batches ----------------------------------------------------------- */

  /**
   * List upload batches, newest first.
   *
   * statusCode is the whole story of a batch: STAGED (uploaded, inert), APPLIED (validated and
   * live), SUPERSEDED (replaced by a later batch), REJECTED (validation refused it). Show it -
   * "I uploaded the file" and "the file is in use" are different states and operators conflate
   * them.
   */
  searchBatches: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/upload/batch?${queryString}`);
  },

  /**
   * One batch in full: the header plus its row-level validation findings.
   *
   * The findings are the reason to open a batch. A batch can upload cleanly and still be wrong -
   * unresolved UserIDs are the common case, and the count of them is what the reject threshold
   * is measured against.
   */
  getBatchDetail: (batchId) => {
    return modelBaseV2.get(`/api/incentive/upload/batch/${batchId}`);
  },

  /* ---- the four upload types --------------------------------------------- */

  /**
   * Platform actuals: F+, Trade-In and SC+ per promoter.
   *
   * ⚠️ These do NOT come from MCS. The Platform Management team owns the AR% of qty for F+ and
   * Trade-In and reports it separately, so this upload is the only way those figures enter FFIS.
   * sourceSystem records which - PLATFORM_MGMT or MANUAL_XLS - against every row, because a
   * hand-keyed spreadsheet and a system feed deserve different trust.
   */
  uploadPlatform: (body) => {
    return modelBaseV2.post('/api/incentive/upload/platform', { body });
  },

  /**
   * KPI measurements per person, for the scorecard schemes (TSE supervisors, SAVINA AV).
   *
   * ⚠️ AN UNKNOWN kpiCode IS FATAL TO THE WHOLE UPLOAD, and deliberately so - unlike an
   * unresolvable UserID, which only means a leaver has nothing to be paid. A KPI with no weight
   * in IncentiveKpiMaster would score zero and quietly lower every total that includes it, so
   * the procedure refuses the file rather than accept a silent under-payment.
   */
  uploadKpi: (body) => {
    return modelBaseV2.post('/api/incentive/upload/kpi', { body });
  },

  /**
   * Model lists: FOCUS | KEY | ELIGIBLE | EXCLUDED | NCME.
   *
   * ⚠️ The procedure REFUSES a file where one model appears on two lists for the same period.
   * That is not a tidiness rule: EXCLUDED and ELIGIBLE are contradictory instructions about
   * whether a model earns anything, and picking either would be a guess about pay.
   */
  uploadEligibleModel: (body) => {
    return modelBaseV2.post('/api/incentive/upload/eligiblemodel', { body });
  },

  /**
   * Manpower: who is in the population, their site, position, grade and compliance flags.
   *
   * ⚠️ THIS IS THE ONLY WRITER OF THE CALCULATOR'S POPULATION. IncentiveFieldForceAttribute is
   * what "who gets paid this period" means, and nothing else fills it - so a period with no
   * manpower batch calculates nobody and reports success while doing it.
   *
   * ⚠️ positionCode VALUES CONTAIN SPACES - "EC REGULAR", "EC AI EXPERT", "SPV TRD", "RSO TRD".
   * Do not trim them to one word or match them case-insensitively against a tidied list; they
   * are the seeded codes and the calculator joins on them exactly.
   */
  uploadManpower: (body) => {
    return modelBaseV2.post('/api/incentive/upload/manpower', { body });
  },

  /* ---- validation: the step that makes an upload real -------------------- */

  /**
   * Validate a staged batch. THIS IS WHAT MAKES IT LIVE, and what supersedes the previous batch.
   *
   * ⚠️ rejectThresholdPct IS THE WRONG-FILE GUARD. A file for the wrong period or the wrong
   * subsidiary resolves almost none of its UserIDs, and applying it would wipe the platform
   * incentive of everyone who should have been in it. The procedure refuses the batch when the
   * unresolved share exceeds this.
   *
   * Pass it explicitly. It defaults to 20 in the procedure ONLY when omitted - an explicit null
   * is sent as null and, before that was coalesced, made the comparison UNKNOWN so the guard
   * silently did not fire and a 100%-unresolved file was applied reporting SUCCESS.
   *
   * ⚠️ 20 IS A PLACEHOLDER. No business tolerance has been agreed and the right figure differs
   * by subsidiary, so show the operator what it is set to rather than hiding it in a default.
   */
  validateBatch: (body) => {
    return modelBaseV2.post('/api/incentive/upload/validate', { body });
  },

  /* ---- manual adjustments ------------------------------------------------ */

  searchAdjustments: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/adjustment?${queryString}`);
  },

  /**
   * Create or edit a manual adjustment. adjustmentId 0 inserts; a real id edits, and only while
   * the row is still PENDING.
   *
   * ⚠️ AN ADJUSTMENT IS NOT VISIBLE ON THE PROMOTER'S PROGRESS SCREEN.
   * IncentiveProgressDailyType has no column for ManualAdjustmentAmt, and table types cannot be
   * ALTERed, so the daily snapshot cannot carry it. It IS in the final payout and the breakdown
   * report. Until that is fixed, an adjusted promoter comparing the app against their payslip
   * finds a difference nothing in the app explains - worth knowing before using these in anger.
   */
  saveAdjustment: (body) => {
    return modelBaseV2.post('/api/incentive/adjustment', { body });
  },
};

export default IncentiveInputModel;
